import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GlobalStyles } from '@m/alchemy-ui/GlobalStyles';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import fetch from 'cross-fetch';
import { enableES5 } from 'immer';
import { ErrorBoundary } from 'react-error-boundary';
import {
  CommunityProvider,
  DialogProvider,
  IntlProvider,
  PageMetadataProvider,
  RuntimeProvider,
  UserProvider
} from '../context';
import { getCommunityTheme } from './theme';
import { type AppProps, defaultRuntimeUser } from './interfaces';
import { ErrorFallback } from './components';
import { CrowdicityGlobalStyle } from './CrowdicityGlobalStyle';
import { GRAPHQL_URI } from './constants';

// Enable ES5 for immer / IE support
enableES5();

const createApolloClient = ({
  communityId,
  sessionId,
  sessionTimeout
}: Pick<AppProps, 'sessionId' | 'sessionTimeout'> & { communityId: AppProps['community']['id'] }) => {
  // The session token is never exposed to JS: it travels as the httpOnly
  // user_pkey cookie, served same-origin via the /graphql proxy.
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-crowdicity-community-id': communityId,
      'x-crowdicity-session-id': sessionId,
      'x-crowdicity-session-timeout': sessionTimeout
    }
  }));

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        User: {
          fields: {
            socialLinks: {
              merge: false
            }
          }
        }
      }
    }),
    link: authLink.concat(
      createHttpLink({
        fetch,
        uri: GRAPHQL_URI,
        credentials: 'include'
      })
    )
  });
};

export const Wrapper = (props: AppProps) => {
  const apiV2Url = String(props.apiV2Url);
  const communityId = String(props.community.id) || '0';
  const sessionId = String(props.sessionId) || '0';
  const sessionTimeout = String(props.sessionTimeout) || '0';
  const cloudinaryUrl = String(props.cloudinaryUrl);
  const mirp = {
    enabled: Boolean(props.mirp.enabled),
    url: String(props.mirp.url),
    region: String(props.mirp.region)
  };
  const locale = props.locale || 'en-GB';
  const user = props?.user || defaultRuntimeUser;

  const handleError = (error: Error) => {
    // Do something with the error
    // E.g. log to an error logging client here
    console.log(error);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <RuntimeProvider
        value={{
          ...props,
          apiV2Url,
          cloudinaryUrl,
          mirp: { ...mirp },
          sessionId,
          sessionTimeout,
          user
        }}
      >
        <ApolloProvider
          client={createApolloClient({
            communityId,
            sessionId,
            sessionTimeout
          })}
        >
          <UserProvider>
            <PageMetadataProvider>
              <CommunityProvider id={communityId}>
                <ThemeProvider
                  theme={getCommunityTheme({
                    communityColors: props.community.colors
                  })}
                >
                  <IntlProvider locale={locale}>
                    <DialogProvider>
                      <GlobalStyles globalCss={CrowdicityGlobalStyle} />
                      {props.children}
                    </DialogProvider>
                  </IntlProvider>
                </ThemeProvider>
              </CommunityProvider>
            </PageMetadataProvider>
          </UserProvider>
        </ApolloProvider>
      </RuntimeProvider>
    </ErrorBoundary>
  );
};
